// React
import React from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
// Material components
import { withStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import OpenIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { Icon } from '@mui/material';
import Pagination from '@mui/lab/Pagination';
// Stores
import LoggedUserStore from 'stores/LoggedUserStore';
import SiteStore from 'stores/SiteStore';
// Action
import SiteAction from 'actions/SiteAction';
// PCP Components
import DialogManageSite from 'components/dialogs/site/DialogManageSite';
import OnMousePopover from 'components/commons/OnMousePopover';
import imageNotFound from 'assets/img/img-not-found.png';
// Theme
import theme from 'assets/theme/theme';
import styles from 'components/page/SiteManagerPage/SiteManagerPage.style';
// Currencies
import currencies from 'currencies.js';

class SitesManagerPage extends React.PureComponent {

    state = {
        sites: SiteStore.getSites(),
        searchText: '',
        isLoading: SiteStore.isLoading(),
        module: 'all',
        dialogManageSiteOpen: false,
        currentSite: null,
        page: 1
    };

    static propTypes = {
        classes: PropTypes.object.isRequired
    };

    componentDidMount() {
        SiteStore.addChangeListener(this.onSiteChange);

        if (SiteStore.getSites().length === 0 && LoggedUserStore.isLogged())
            SiteAction.getSites();

        this.mounted = true;
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    onSiteChange = () => {
        if (!this.mounted) {
            return false;
        }

        this.setState({
            sites: SiteStore.getSites(),
            isLoading: SiteStore.isLoading()
        });
    };

    updateSite = id => event => {
        const site = this.state.sites.find(s => s.id === id);
        SiteAction.updateSite({ [event.target.value]: !site[event.target.value] }, id);
    };

    deleteSite = id => {
        SiteAction.deleteSite(id);
    };

    close = () => {
        this.setState({ dialogManageSiteOpen: false, currentSite: null });
    };

    getContrastYIQ = (hexcolor) => {
        const colorWithoutHash = hexcolor.replace('#', '');
        const r = parseInt(colorWithoutHash.substr(0, 2), 16);
        const g = parseInt(colorWithoutHash.substr(2, 2), 16);
        const b = parseInt(colorWithoutHash.substr(4, 2), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return yiq >= 128 ? '#000000' : '#fff';
    };

    renderDialogManageSite = () =>
        <DialogManageSite
            open={this.state.dialogManageSiteOpen}
            close={this.close}
            site={this.state.currentSite}
            key={this.state.currentSite ? this.state.currentSite.id : null}
        />;

    render() {
        const { classes } = this.props;
        const { page } = this.state;

        if (!LoggedUserStore.isLogged()) {
            return (
                <Redirect to='/login' />
            );
        }

        let sites = this.state.sites.slice(0);
        sites = sites.filter(site => site.name.toLowerCase().includes(this.state.searchText.toLowerCase()));

        return (
            <div className={classes.root}>
                <div className={classes.header}>
                    <Button
                        variant='contained'
                        color='primary'
                        className={classes.button} onClick={() => this.setState({ dialogManageSiteOpen: true })}>
                        <AddIcon className={classes.leftIcon} />
                        Site
                    </Button>
                    <TextField
                        id='searchfield'
                        className={classes.searchBar}
                        variant='outlined'
                        value={this.state.searchText}
                        size= 'small'
                        onChange={event => this.setState({ searchText: event.target.value })}
                        placeholder={'Site\'s name'}
                        margin={'dense'}
                        InputProps={{
                            startAdornment: <InputAdornment position='start'><SearchIcon className={classes.searchIcon} /></InputAdornment>
                        }}
                    />
                </div>
                <Paper className={classes.paper}>
                    {this.state.isLoading ?
                        <div className={classes.loaderContainer}><CircularProgress /></div>
                        :
                        <div className={classes.tableContainer}>
                            <Table className={classes.table} stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align='left' className={classes.tableHeadCell} style={{ paddingLeft: '15px' }}>Site</TableCell>
                                        <TableCell align='left' className={classes.tableHeadCell}><Icon color='primary'>euro_symbol</Icon></TableCell>
                                        <TableCell align='left' className={classes.tableHeadCell}><Icon color='primary'>trending_up</Icon></TableCell>
                                        <TableCell align='left' className={classes.tableHeadCell}><Icon color='primary'>calendar_view_day</Icon></TableCell>
                                        <TableCell align='left' className={classes.tableHeadCell}><Icon color='primary'>mail</Icon></TableCell>
                                        <TableCell align='center' className={classes.tableHeadCell}>Url</TableCell>
                                        <TableCell align='center' className={classes.tableHeadCell}>Type</TableCell>
                                        <TableCell align='center' className={classes.tableHeadCell}>Threshold</TableCell>
                                        <TableCell align='center' className={classes.tableHeadCell}>Currency</TableCell>
                                        <TableCell align='center' className={classes.tableHeadCell}>Color</TableCell>
                                        <TableCell align='center' className={classes.tableHeadCell}>Edit</TableCell>
                                        <TableCell align='center' className={classes.tableHeadCell}>Delete</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sites.slice((page - 1) * 100, page * 100).map((site, key) => <TableRow key={site.id} style={{ backgroundColor: key % 2 === 0 ? theme.palette.grey.semilight : theme.palette.white.main }}>
                                        <TableCell align='left' className={classes.basicCell} style={{ width: '270px' }}>
                                            <div className={classes.nameCell}>
                                                <img src={`https://img.pricecomparator.pro/sites_images/${site.name}/1?$${Date.now()}`} width={30} style={{ marginRight: '5px', marginLeft: '5px' }}
                                                    onError={(error) => {
                                                        error.target.src = imageNotFound;
                                                    }}></img>
                                                {site.name}
                                            </div>
                                        </TableCell>
                                        {site.associatedAccounts.spe && <TableCell align='left' style={{ paddingLeft: '16px' }} className={classes.basicCell}>{site.associatedAccounts.spe.count > 0 ? <OnMousePopover text={site.associatedAccounts.spe.count}><Typography style={{ fontWeight: 600 }}>SPE</Typography>{site.associatedAccounts.spe.accounts.map(a => <Typography key={a}>{a}</Typography>)}</OnMousePopover> : '-'}</TableCell>}
                                        {site.associatedAccounts.perf && <TableCell align='left' style={{ paddingLeft: '16px' }} className={classes.basicCell}>{site.associatedAccounts.perf.count > 0 ? <OnMousePopover text={site.associatedAccounts.perf.count}><Typography style={{ fontWeight: 600 }}>PERF</Typography>{site.associatedAccounts.perf.accounts.map(a => <Typography key={a}>{a}</Typography>)}</OnMousePopover> : '-'}</TableCell>}
                                        {site.associatedAccounts.slider && <TableCell align='left' style={{ paddingLeft: '16px' }} className={classes.basicCell}>{site.associatedAccounts.slider.count > 0 ? <OnMousePopover text={site.associatedAccounts.slider.count}><Typography style={{ fontWeight: 600 }}>SLIDER</Typography>{site.associatedAccounts.slider.accounts.map(a => <Typography key={a}>{a}</Typography>)}</OnMousePopover> : '-'}</TableCell>}
                                        {site.associatedAccounts.adbox && <TableCell align='left' style={{ paddingLeft: '16px' }} className={classes.basicCell}>{site.associatedAccounts.adbox.count > 0 ? <OnMousePopover text={site.associatedAccounts.adbox.count}><Typography style={{ fontWeight: 600 }}>ADBOX</Typography>{site.associatedAccounts.adbox.accounts.map(a => <Typography key={a}>{a}</Typography>)}</OnMousePopover> : '-'}</TableCell>}
                                        <TableCell align='left' className={classes.urlCell}><div className={classes.urlContainer}>{site.url}<OpenIcon onClick={() => window.open(site.url)} className={classes.openIcon} /></div></TableCell>
                                        <TableCell align='center' className={classes.basicCell}>{site.speConfig ? site.speConfig.isMarketplace ? 'Marketplace' : 'Normal' : '-'}</TableCell>
                                        <TableCell align='center' className={classes.basicCell}>{site.speConfig && site.speConfig.trustAlertThreshold ? site.speConfig.trustAlertThreshold : '-'}</TableCell>
                                        <TableCell align='center' className={classes.basicCell}>{site.speConfig && site.speConfig.currency ? currencies.find(c => site.speConfig.currency === c.code) ? currencies.find(c => site.speConfig.currency === c.code).symbol : '???' : '-'}</TableCell>
                                        <TableCell align='center' className={classes.basicCell}>{site.color ? <Chip size='small' label={site.color} style={{ backgroundColor: site.color, color: site.color ? this.getContrastYIQ(site.color) : undefined }} /> : '-'}</TableCell>
                                        <TableCell align='center' className={classes.basicCell} style={{ width: '60px' }}><IconButton aria-label='delete' onClick={() => this.setState({ currentSite: site, dialogManageSiteOpen: true })}>
                                            <EditIcon fontSize='small' />
                                        </IconButton></TableCell>
                                        <TableCell align='center' className={classes.basicCell} style={{ width: '60px' }}><IconButton aria-label='delete' disabled>
                                            <DeleteIcon fontSize='small' />
                                        </IconButton></TableCell>
                                    </TableRow>)}
                                </TableBody>
                            </Table>
                        </div>
                    }
                    <Pagination page={this.state.page} count={Math.ceil(sites.length / 100)} style={{ marginLeft: 'auto', marginTop: '10px' }} onChange={(e, p) => this.setState({ page: p })} />
                </Paper>
                {this.renderDialogManageSite()}
            </div>
        );
    }
}

export default withStyles(styles)(SitesManagerPage);
